import { REFRIGERANT_PT_TABLES } from "./refrigerantTables";

const CONDENSER_APPROACH_LIMIT_C = 3;
const REQUIRED_CONSECUTIVE_RECORDS = 3;

// Reglas especiales para Victex 1Comp enfriada por aire.
const VICTEX_HOT_AMBIENT_C = 35;
const VICTEX_PRESSURE_LIMIT_NORMAL_PSIG = 205;
const VICTEX_PRESSURE_LIMIT_HOT_PSIG = 200;

export function toAlgorithmNumber(value) {
  if (value == null || value === "") {
    return null;
  }

  const numberValue = Number(
    String(value).trim().replace(",", ".")
  );

  return Number.isFinite(numberValue)
    ? numberValue
    : null;
}

const STANDARD_ATMOSPHERIC_PRESSURE_PSI = 14.6959;

function psiaToPsig(value) {
  const pressurePsia =
    toAlgorithmNumber(value);

  if (pressurePsia == null) {
    return null;
  }

  return Number(
    (
      pressurePsia -
      STANDARD_ATMOSPHERIC_PRESSURE_PSI
    ).toFixed(2)
  );
}

/**
 * Convierte una presión en psig a temperatura de saturación.
 * Cuando la presión cae entre dos puntos, realiza interpolación lineal.
 */
export function getSaturationTemperatureC({
  refrigerant,
  pressurePsig,
}) {
  const pressure = toAlgorithmNumber(pressurePsig);
  const table = REFRIGERANT_PT_TABLES[refrigerant];

  if (
    pressure == null ||
    !Array.isArray(table) ||
    table.length < 2
  ) {
    return null;
  }

  const firstPoint = table[0];
  const lastPoint = table[table.length - 1];

  if (
    pressure < firstPoint.pressurePsig ||
    pressure > lastPoint.pressurePsig
  ) {
    return null;
  }

  for (let index = 1; index < table.length; index += 1) {
    const lowerPoint = table[index - 1];
    const upperPoint = table[index];

    const isInsideRange =
      pressure >= lowerPoint.pressurePsig &&
      pressure <= upperPoint.pressurePsig;

    if (!isInsideRange) {
      continue;
    }

    const pressureDifference =
      upperPoint.pressurePsig -
      lowerPoint.pressurePsig;

    if (pressureDifference === 0) {
      return lowerPoint.tempC;
    }

    const position =
      (pressure - lowerPoint.pressurePsig) /
      pressureDifference;

    const temperature =
      lowerPoint.tempC +
      position *
        (upperPoint.tempC - lowerPoint.tempC);

    return Number(temperature.toFixed(2));
  }

  return null;
}

function buildNoDataResult({
  key,
  title,
  message,
  cause,
  load,
}) {
  return {
    key,
    title,
    status: "nodata",
    message,
    cause,
    resultLabel: "Resultado",
    deltaT: null,
    expectedMin: 0,
    expectedMax: CONDENSER_APPROACH_LIMIT_C,
    alarmMin: CONDENSER_APPROACH_LIMIT_C,
    loads: load == null ? [] : [load],
  };
}

function evaluateCondenserFoulingCircuit({
  machineKey,
  machineTitle,
  compressorNumber,
  refrigerant,
  dischargePressure,
  towerOutletTemperature,
  compressorLoad,
  outletTemperatureLabel = "Temperatura de salida de torre",
}) {
  const pressurePsig =
    toAlgorithmNumber(dischargePressure);

  const towerOutletC =
    toAlgorithmNumber(towerOutletTemperature);

  const load =
    toAlgorithmNumber(compressorLoad);

  const key =
    `${machineKey}_condenser_fouling_c${compressorNumber}`;

  const title =
    `${machineTitle} Suciedad condensador C${compressorNumber}`;

  if (load == null) {
    return buildNoDataResult({
      key,
      title,
      message: "No hay porcentaje de carga.",
      cause:
        `No fue posible determinar si el compresor ${compressorNumber} está encendido.`,
      load,
    });
  }

  // Compresor apagado. No genera tarjeta.
  if (load <= 0) {
    return null;
  }

  if (
    pressurePsig == null ||
    towerOutletC == null
  ) {
    return buildNoDataResult({
      key,
      title,
      message:
        "No hay suficientes datos para evaluar.",
      cause:
        `Falta presión de descarga o ${outletTemperatureLabel.toLowerCase()}.`,
      load,
    });
  }

  const saturationTemperatureC =
    getSaturationTemperatureC({
      refrigerant,
      pressurePsig,
    });

  if (saturationTemperatureC == null) {
    return buildNoDataResult({
      key,
      title,
      message:
        "Presión fuera del rango de la tabla.",
      cause:
        `La presión de descarga de ${pressurePsig.toFixed(1)} psig ` +
        `no se encuentra dentro de la tabla ${refrigerant}.`,
      load,
    });
  }

  const condenserApproachC = Number(
    (
      saturationTemperatureC -
      towerOutletC
    ).toFixed(2)
  );

  let status = "ok";
  let message =
    "Condensador dentro de parámetros.";
  let resultLabel = "Resultado";

  const calculationDetails =
    `Presión de descarga: ${pressurePsig.toFixed(1)} psig. ` +
    `Temperatura de saturación ${refrigerant}: ` +
    `${saturationTemperatureC.toFixed(1)} °C. ` +
    `${outletTemperatureLabel}: ` +
    `${towerOutletC.toFixed(1)} °C. ` +
    `Diferencial: ${condenserApproachC.toFixed(1)} °C.`;

  let cause =
    `El diferencial no supera ` +
    `${CONDENSER_APPROACH_LIMIT_C} °C. ` +
    calculationDetails;

  if (condenserApproachC < 0) {
    status = "warn";
    message =
      "Lecturas de condensador no coherentes.";
    resultLabel = "Revisión";

    cause =
    `${outletTemperatureLabel} es mayor que ` +
    "la temperatura de saturación calculada. " +
    "Revisar sensores, unidades y sentido de las temperaturas. " +
    calculationDetails;
  } else if (
    condenserApproachC >
    CONDENSER_APPROACH_LIMIT_C
  ) {
    /*
     * Aquí se marca temporalmente como alarm.
     * evaluateCondenserFoulingTrend después decidirá
     * si queda en warn o se confirma como alarm.
     */
    status = "alarm";
    message =
      "Posible suciedad en condensador.";
    resultLabel = "Posible causa";

    cause =
      `El diferencial supera ` +
      `${CONDENSER_APPROACH_LIMIT_C} °C. ` +
      calculationDetails;
  }

  return {
    key,
    title,
    status,
    message,
    cause,
    resultLabel,

    deltaT: condenserApproachC,
    expectedMin: 0,
    expectedMax: CONDENSER_APPROACH_LIMIT_C,
    alarmMin: CONDENSER_APPROACH_LIMIT_C,
    loads: [load],

    compressorNumber,
    refrigerant,
    dischargePressurePsig: pressurePsig,
    saturationTemperatureC,
    towerOutletTemperatureC: towerOutletC,
  };
}

function getRowTimestamp(row) {
  const value = row?.fecha;

  if (value instanceof Date) {
    return value.getTime();
  }

  if (value == null || value === "") {
    return 0;
  }

  // Convierte "YYYY-MM-DD HH:mm:ss" a un formato
  // que JavaScript puede interpretar correctamente.
  const normalizedValue = String(value)
    .trim()
    .replace(" ", "T");

  const timestamp = Date.parse(normalizedValue);

  return Number.isFinite(timestamp)
    ? timestamp
    : 0;
}

function evaluateCondenserFoulingTrend({
  rows,
  machineKey,
  machineTitle,
  compressorNumber,
  refrigerant,
  dischargePressureField,
  towerOutletTemperatureField,
  compressorLoadField,
  outletTemperatureLabel = "Temperatura de salida de torre",
}) {
  const orderedRows = Array.isArray(rows)
    ? [...rows]
        .filter(Boolean)
        .sort(
          (rowA, rowB) =>
            getRowTimestamp(rowB) -
            getRowTimestamp(rowA)
        )
    : [];

  // Solamente se toman los tres registros más recientes.
  const recentRows = orderedRows.slice(
    0,
    REQUIRED_CONSECUTIVE_RECORDS
  );

  if (recentRows.length === 0) {
    return null;
  }

  const evaluations = recentRows.map((row) =>
    evaluateCondenserFoulingCircuit({
      machineKey,
      machineTitle,
      compressorNumber,
      refrigerant,

      dischargePressure:
        row?.[dischargePressureField],

      towerOutletTemperature:
        row?.[towerOutletTemperatureField],

      compressorLoad:
        row?.[compressorLoadField],

      outletTemperatureLabel,
    })
  );

  // La primera evaluación corresponde al registro más reciente.
  const currentResult = evaluations[0];

  // El compresor está apagado en el registro actual.
  if (!currentResult) {
    return null;
  }

  const validEvaluations = evaluations.filter(
    (result) =>
      result &&
      result.status !== "nodata" &&
      result.deltaT != null &&
      result.deltaT >= 0
  );

  /*
   * Cuenta registros fuera de rango empezando por el más reciente.
   * Se detiene cuando encuentra:
   *
   * Un compresor apagado
   * Un registro inválido
   * Un diferencial normal
   * Un diferencial negativo
   */
  let consecutiveOutOfRangeCount = 0;

  for (const result of evaluations) {
    const isValid =
      result &&
      result.status !== "nodata" &&
      result.deltaT != null &&
      result.deltaT >= 0;

    const isOutOfRange =
      isValid &&
      result.deltaT >
        CONDENSER_APPROACH_LIMIT_C;

    if (!isOutOfRange) {
      break;
    }

    consecutiveOutOfRangeCount += 1;
  }

  const alarmConfirmed =
    recentRows.length ===
      REQUIRED_CONSECUTIVE_RECORDS &&
    consecutiveOutOfRangeCount ===
      REQUIRED_CONSECUTIVE_RECORDS;

  const recentApproachesC = evaluations.map(
    (result) => result?.deltaT ?? null
  );

  const trendDetails =
    `Registros consecutivos fuera de rango: ` +
    `${consecutiveOutOfRangeCount} de ` +
    `${REQUIRED_CONSECUTIVE_RECORDS}. ` +
    `Registros válidos disponibles: ` +
    `${validEvaluations.length} de ` +
    `${REQUIRED_CONSECUTIVE_RECORDS}.`;

  // Conserva el resultado de falta de datos.
  if (
    currentResult.status === "nodata" ||
    currentResult.deltaT == null
  ) {
    return {
      ...currentResult,
      cause:
        `${currentResult.cause} ${trendDetails}`,

      consecutiveOutOfRangeCount,
      consecutiveValidRecords:
        validEvaluations.length,
      requiredConsecutiveRecords:
        REQUIRED_CONSECUTIVE_RECORDS,
      recentApproachesC,
      alarmConfirmed: false,
    };
  }

  // Conserva la advertencia de una lectura negativa.
  if (currentResult.deltaT < 0) {
    return {
      ...currentResult,
      cause:
        `${currentResult.cause} ${trendDetails}`,

      consecutiveOutOfRangeCount,
      consecutiveValidRecords:
        validEvaluations.length,
      requiredConsecutiveRecords:
        REQUIRED_CONSECUTIVE_RECORDS,
      recentApproachesC,
      alarmConfirmed: false,
    };
  }

  const currentCalculation =
    `Presión actual: ` +
    `${currentResult.dischargePressurePsig.toFixed(1)} psig. ` +
    `Temperatura de saturación ${refrigerant}: ` +
    `${currentResult.saturationTemperatureC.toFixed(1)} °C. ` +
    `${outletTemperatureLabel}: ` +
    `${currentResult.towerOutletTemperatureC.toFixed(1)} °C. ` +
    `Diferencial actual: ` +
    `${currentResult.deltaT.toFixed(1)} °C.`;

  let status = "ok";
  let message =
    "Condensador dentro de parámetros.";
  let resultLabel = "Resultado";

  let cause =
    `El diferencial actual no supera ` +
    `${CONDENSER_APPROACH_LIMIT_C} °C. ` +
    `${currentCalculation} ${trendDetails}`;

  if (alarmConfirmed) {
    status = "alarm";
    message =
      "Posible suciedad en condensador.";
    resultLabel = "Posible causa";

    cause =
      `Los últimos ` +
      `${REQUIRED_CONSECUTIVE_RECORDS} registros ` +
      `consecutivos superan ` +
      `${CONDENSER_APPROACH_LIMIT_C} °C. ` +
      "Posible incrustación o suciedad en los tubos o serpentín. " +
      `${currentCalculation} ${trendDetails}`;
  } else if (
    currentResult.deltaT >
    CONDENSER_APPROACH_LIMIT_C
  ) {
    status = "warn";
    message =
      "Diferencial elevado en observación.";
    resultLabel = "Seguimiento";

    cause =
      `El diferencial actual supera ` +
      `${CONDENSER_APPROACH_LIMIT_C} °C, ` +
      `pero todavía no existen ` +
      `${REQUIRED_CONSECUTIVE_RECORDS} registros ` +
      `consecutivos fuera de rango para confirmar la alarma. ` +
      `${currentCalculation} ${trendDetails}`;
  }

  return {
    ...currentResult,

    status,
    message,
    cause,
    resultLabel,

    consecutiveOutOfRangeCount,
    consecutiveValidRecords:
      validEvaluations.length,
    requiredConsecutiveRecords:
      REQUIRED_CONSECUTIVE_RECORDS,
    recentApproachesC,
    alarmConfirmed,
  };
}

function evaluateTwoCompCondenserFouling({
  rows,
  machineKey,
  machineTitle,
}) {
  const results = [
    evaluateCondenserFoulingTrend({
      rows,

      machineKey,
      machineTitle,

      compressorNumber: 1,
      refrigerant: "R134A",

      dischargePressureField:
        "presionDescargaComp1",

      towerOutletTemperatureField:
        "tempTorreSalida",

      compressorLoadField:
        "porcentajeCargaComp1",
    }),

    evaluateCondenserFoulingTrend({
      rows,

      machineKey,
      machineTitle,

      compressorNumber: 2,
      refrigerant: "R134A",

      dischargePressureField:
        "presionDescargaComp2",

      towerOutletTemperatureField:
        "tempTorreSalida",

      compressorLoadField:
        "porcentajeCargaComp2",
    }),
  ];

  // Elimina las tarjetas de compresores apagados.
  return results.filter(Boolean);
}

function evaluateOneCompCondenserFouling({
  rows,
  machineKey,
  machineTitle,
  dischargePressureField = "presionDescargaComp1",
}) {
  const results = [
    evaluateCondenserFoulingTrend({
      rows,

      machineKey,
      machineTitle,

      compressorNumber: 1,
      refrigerant: "R134A",

      dischargePressureField,

      towerOutletTemperatureField:
        "tempTorreSalida",

      compressorLoadField:
        "porcentajeCargaComp1",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateCoTherm2CompCondenserFouling(
  rows
) {
  const results = [
    evaluateCondenserFoulingTrend({
      rows,

      machineKey: "cotherm2comp",
      machineTitle: "CoTherm 2Comp",

      compressorNumber: 1,
      refrigerant: "R134A",

      dischargePressureField:
        "presionDescargaComp1",

      towerOutletTemperatureField:
        "tempProcesoSalida",

      compressorLoadField:
        "porcentajeCargaComp1",

      outletTemperatureLabel:
        "Temperatura de salida de agua caliente",
    }),

    evaluateCondenserFoulingTrend({
      rows,

      machineKey: "cotherm2comp",
      machineTitle: "CoTherm 2Comp",

      compressorNumber: 2,
      refrigerant: "R134A",

      dischargePressureField:
        "presionDescargaComp2",

      towerOutletTemperatureField:
        "tempProcesoSalida",

      compressorLoadField:
        "porcentajeCargaComp2",

      outletTemperatureLabel:
        "Temperatura de salida de agua caliente",
    }),
  ];

  return results.filter(Boolean);
}

export function evaluateOptichiller2CompCondenserFouling(
  rows
) {
  return evaluateTwoCompCondenserFouling({
    rows,
    machineKey: "optichiller2comp",
    machineTitle: "Optichiller 2Comp",
  });
}

export function evaluateOptichiller2CompCT6CondenserFouling(
  rows
) {
  return evaluateTwoCompCondenserFouling({
    rows,
    machineKey: "optichiller_ct6",
    machineTitle: "Optichiller 2Comp CT6",
  });
}

export function evaluateOptichiller1CompCondenserFouling(
  rows
) {
  return evaluateOneCompCondenserFouling({
    rows,
    machineKey: "optichiller1comp",
    machineTitle: "Optichiller 1Comp",
  });
}

export function evaluateOptichiller1CompCT6CondenserFouling(
  rows
) {
  return evaluateOneCompCondenserFouling({
    rows,
    machineKey: "optichiller1comp_ct6",
    machineTitle: "Optichiller 1Comp CT6",
    dischargePressureField:
      "presDescargaComp1",
  });
}

export function evaluateLicuador1CompCondenserFouling(
  rows
) {
  const results = [
    evaluateCondenserFoulingTrend({
      rows,

      machineKey: "licuador1comp",
      machineTitle: "Licuador 1Comp",

      compressorNumber: 1,
      refrigerant: "R507A",

      dischargePressureField:
        "presDescarga",

      towerOutletTemperatureField:
        "tempCondSalida",

      compressorLoadField:
        "porcentajeCargaComp1",

      outletTemperatureLabel:
        "Temperatura de salida de condensación",
    }),
  ];

  return results.filter(Boolean);
}

function evaluateVictexCondenserRow(row) {
  const ambientTemperatureC =
    toAlgorithmNumber(row?.tempAmbiente);

  const dischargePressurePsig =
    toAlgorithmNumber(row?.presionDescarga);

  const compressorLoad =
    toAlgorithmNumber(row?.porcentajeCargaComp1);

  const key =
    "victex1comp_condenser_fouling";

  const title =
    "Victex 1Comp Suciedad condensador";

  // No sabemos si el compresor está operando.
  if (compressorLoad == null) {
    return {
      key,
      title,
      status: "nodata",
      message: "No hay porcentaje de carga.",
      cause:
        "No fue posible determinar si el compresor está encendido.",
      resultLabel: "Resultado",
      deltaT: null,
      expectedMin: null,
      expectedMax: null,
      alarmMin: null,
      loads: [],
      ambientTemperatureC,
      dischargePressurePsig,
      alarmCandidate: false,
    };
  }

  // Compresor apagado. No genera tarjeta.
  if (compressorLoad <= 0) {
    return null;
  }

  if (
    ambientTemperatureC == null ||
    dischargePressurePsig == null
  ) {
    return {
      key,
      title,
      status: "nodata",
      message:
        "No hay suficientes datos para evaluar.",
      cause:
        "Falta temperatura ambiente o presión de descarga.",
      resultLabel: "Resultado",
      deltaT: null,
      expectedMin: null,
      expectedMax: null,
      alarmMin: null,
      loads: [compressorLoad],
      ambientTemperatureC,
      dischargePressurePsig,
      alarmCandidate: false,
    };
  }

  const hotAmbient =
    ambientTemperatureC >= VICTEX_HOT_AMBIENT_C;

  const pressureLimitPsig =
    hotAmbient
      ? VICTEX_PRESSURE_LIMIT_HOT_PSIG
      : VICTEX_PRESSURE_LIMIT_NORMAL_PSIG;

  const pressureHigh =
    dischargePressurePsig > pressureLimitPsig;

  let status = "ok";
  let message =
    "Condensador dentro de parámetros.";
  let resultLabel = "Resultado";

  let cause =
    `Temperatura ambiente: ${ambientTemperatureC.toFixed(1)} °C. ` +
    `Presión de descarga: ${dischargePressurePsig.toFixed(1)} psig. ` +
    `Límite actual: ${pressureLimitPsig} psig.`;

  /*
   * Ambiente menor a 35 °C:
   * presión > 205 psig es candidata a alarma.
   * La tendencia de 3 registros decide si se confirma.
   */
  if (!hotAmbient && pressureHigh) {
    status = "alarm";
    message =
      "Posible suciedad en condensador.";
    resultLabel = "Posible causa";

    cause =
      `La temperatura ambiente es menor a ` +
      `${VICTEX_HOT_AMBIENT_C} °C y la presión de descarga ` +
      `supera ${VICTEX_PRESSURE_LIMIT_NORMAL_PSIG} psig. ` +
      `Temperatura ambiente: ${ambientTemperatureC.toFixed(1)} °C. ` +
      `Presión de descarga: ${dischargePressurePsig.toFixed(1)} psig.`;
  }

  /*
   * Ambiente de 35 °C o mayor:
   * presión > 200 psig queda como advertencia porque
   * el calor ambiente también puede elevar la presión.
   */
  if (hotAmbient && pressureHigh) {
    status = "warn";
    message =
      "Ambiente caluroso con posible suciedad en condensador.";
    resultLabel = "Revisión";

    cause =
      `La temperatura ambiente es de ` +
      `${ambientTemperatureC.toFixed(1)} °C y la presión de descarga ` +
      `supera ${VICTEX_PRESSURE_LIMIT_HOT_PSIG} psig. ` +
      `La presión puede estar elevada por la temperatura ambiente, ` +
      `pero también puede existir suciedad en el condensador. ` +
      `Presión actual: ${dischargePressurePsig.toFixed(1)} psig.`;
  }

  return {
    key,
    title,
    status,
    message,
    cause,
    resultLabel,

    // Este algoritmo no trabaja con diferencial de temperatura.
    deltaT: null,
    expectedMin: null,
    expectedMax: pressureLimitPsig,
    alarmMin:
      hotAmbient
        ? null
        : VICTEX_PRESSURE_LIMIT_NORMAL_PSIG,

    loads: [compressorLoad],

    ambientTemperatureC,
    dischargePressurePsig,
    pressureLimitPsig,
    hotAmbient,

    // Solo esta condición puede convertirse en alarma.
    alarmCandidate:
      !hotAmbient && pressureHigh,
  };
}


export function evaluateVictexCondenserFouling(
  rows
) {
  const orderedRows = Array.isArray(rows)
    ? [...rows]
        .filter(Boolean)
        .sort(
          (rowA, rowB) =>
            getRowTimestamp(rowB) -
            getRowTimestamp(rowA)
        )
    : [];

  const recentRows = orderedRows.slice(
    0,
    REQUIRED_CONSECUTIVE_RECORDS
  );

  if (recentRows.length === 0) {
    return [];
  }

  const evaluations = recentRows.map((row) =>
    evaluateVictexCondenserRow(row)
  );

  // Primer elemento = registro más reciente.
  const currentResult = evaluations[0];

  // Compresor apagado actualmente.
  if (!currentResult) {
    return [];
  }

  let consecutiveAlarmConditionCount = 0;

  /*
   * Para confirmar suciedad necesitamos:
   *
   * Ambiente < 35 °C
   * Presión > 205 psig
   *
   * durante 3 registros consecutivos.
   */
  for (const result of evaluations) {
    if (
      !result ||
      result.status === "nodata" ||
      !result.alarmCandidate
    ) {
      break;
    }

    consecutiveAlarmConditionCount += 1;
  }

  const alarmConfirmed =
    recentRows.length ===
      REQUIRED_CONSECUTIVE_RECORDS &&
    consecutiveAlarmConditionCount ===
      REQUIRED_CONSECUTIVE_RECORDS;

  const recentPressuresPsig =
    evaluations.map(
      (result) =>
        result?.dischargePressurePsig ?? null
    );

  const recentAmbientTemperaturesC =
    evaluations.map(
      (result) =>
        result?.ambientTemperatureC ?? null
    );

  const trendDetails =
    `Registros consecutivos con condición de suciedad: ` +
    `${consecutiveAlarmConditionCount} de ` +
    `${REQUIRED_CONSECUTIVE_RECORDS}.`;

  // Falta información.
  if (currentResult.status === "nodata") {
    return [
      {
        ...currentResult,

        cause:
          `${currentResult.cause} ${trendDetails}`,

        consecutiveAlarmConditionCount,
        requiredConsecutiveRecords:
          REQUIRED_CONSECUTIVE_RECORDS,
        recentPressuresPsig,
        recentAmbientTemperaturesC,
        alarmConfirmed: false,
      },
    ];
  }

  /*
   * Ambiente >= 35 °C y presión > 200.
   *
   * Siempre se queda en WARN porque el calor ambiente
   * también puede explicar la presión elevada.
   */
  if (
    currentResult.hotAmbient &&
    currentResult.dischargePressurePsig >
      VICTEX_PRESSURE_LIMIT_HOT_PSIG
  ) {
    return [
      {
        ...currentResult,

        status: "warn",
        message:
          "Ambiente caluroso con posible suciedad en condensador.",
        resultLabel: "Revisión",

        cause:
          `${currentResult.cause} ${trendDetails}`,

        consecutiveAlarmConditionCount,
        requiredConsecutiveRecords:
          REQUIRED_CONSECUTIVE_RECORDS,
        recentPressuresPsig,
        recentAmbientTemperaturesC,
        alarmConfirmed: false,
      },
    ];
  }

  /*
   * Ambiente < 35 °C y presión > 205.
   * Solamente pasa a ALARM si son 3 consecutivos.
   */
  if (currentResult.alarmCandidate) {
    if (alarmConfirmed) {
      return [
        {
          ...currentResult,

          status: "alarm",
          message:
            "Posible suciedad en condensador.",
          resultLabel: "Posible causa",

          cause:
            `Los últimos ${REQUIRED_CONSECUTIVE_RECORDS} registros ` +
            `consecutivos tienen temperatura ambiente menor a ` +
            `${VICTEX_HOT_AMBIENT_C} °C y presión de descarga mayor a ` +
            `${VICTEX_PRESSURE_LIMIT_NORMAL_PSIG} psig. ` +
            `Temperatura ambiente actual: ` +
            `${currentResult.ambientTemperatureC.toFixed(1)} °C. ` +
            `Presión actual: ` +
            `${currentResult.dischargePressurePsig.toFixed(1)} psig. ` +
            `${trendDetails}`,

          consecutiveAlarmConditionCount,
          requiredConsecutiveRecords:
            REQUIRED_CONSECUTIVE_RECORDS,
          recentPressuresPsig,
          recentAmbientTemperaturesC,
          alarmConfirmed: true,
        },
      ];
    }

    return [
      {
        ...currentResult,

        status: "warn",
        message:
          "Presión de descarga elevada en observación.",
        resultLabel: "Seguimiento",

        cause:
          `La temperatura ambiente actual es menor a ` +
          `${VICTEX_HOT_AMBIENT_C} °C y la presión supera ` +
          `${VICTEX_PRESSURE_LIMIT_NORMAL_PSIG} psig, ` +
          `pero todavía no existen ` +
          `${REQUIRED_CONSECUTIVE_RECORDS} registros consecutivos ` +
          `para confirmar posible suciedad. ` +
          `Temperatura ambiente actual: ` +
          `${currentResult.ambientTemperatureC.toFixed(1)} °C. ` +
          `Presión actual: ` +
          `${currentResult.dischargePressurePsig.toFixed(1)} psig. ` +
          `${trendDetails}`,

        consecutiveAlarmConditionCount,
        requiredConsecutiveRecords:
          REQUIRED_CONSECUTIVE_RECORDS,
        recentPressuresPsig,
        recentAmbientTemperaturesC,
        alarmConfirmed: false,
      },
    ];
  }

  // Condición normal.
  return [
    {
      ...currentResult,

      status: "ok",
      message:
        "Condensador dentro de parámetros.",
      resultLabel: "Resultado",

      cause:
        `${currentResult.cause} ${trendDetails}`,

      consecutiveAlarmConditionCount,
      requiredConsecutiveRecords:
        REQUIRED_CONSECUTIVE_RECORDS,
      recentPressuresPsig,
      recentAmbientTemperaturesC,
      alarmConfirmed: false,
    },
  ];
}

export function evaluateCentrifugaCondenserFouling(
  rows
) {
  /*
   * La Centrífuga entrega presión absoluta PSIA.
   *
   * Nuestra tabla PT de R134A trabaja en PSIG,
   * por lo que primero convertimos:
   *
   * PSIG = PSIA - 14.6959
   */
  const convertedRows = Array.isArray(rows)
    ? rows.map((row) => {
        const tempTorreSalida =
          toAlgorithmNumber(
            row?.tempAguaTorreSalida
          );

        const slideValve =
          toAlgorithmNumber(
            row?.posicionSlideValve
          );

        /*
        * Valores claramente inválidos.
        */
        const tempTorreSalidaValida =
          tempTorreSalida != null &&
          tempTorreSalida !== 999 &&
          tempTorreSalida !== -999
            ? tempTorreSalida
            : null;

        const slideValveValida =
          slideValve != null &&
          slideValve >= 0 &&
          slideValve <= 100
            ? slideValve
            : null;

        return {
          ...row,

          presionDescargaPsig:
            psiaToPsig(
              row?.presAbsolutaDescarga
            ),

          tempTorreSalidaFouling:
            tempTorreSalidaValida,

          cargaCentrifuga:
            slideValveValida,
        };
      })
    : [];

  const result =
    evaluateCondenserFoulingTrend({
      rows: convertedRows,

      machineKey: "centrifuga",
      machineTitle: "Centrífuga",

      compressorNumber: 1,

      refrigerant: "R134A",

      // YA CONVERTIDA DE PSIA A PSIG
      dischargePressureField:
        "presionDescargaPsig",

      towerOutletTemperatureField:
        "tempAguaTorreSalida",

      compressorLoadField:
        "cargaCentrifuga",

      outletTemperatureLabel:
        "Temperatura de salida de agua de torre",
    });

  return result ? [result] : [];
}